/**
 * useMediaRecorder — push-to-talk audio capture hook.
 *
 * Usage:
 *   const { isRecording, isSupported, start, stop, durationMs, error } = useMediaRecorder();
 *   await start();           // requests mic permission, begins recording
 *   const blob = await stop(); // resolves to the audio Blob (audio/webm on Chrome)
 *
 * Browser notes:
 *   - Chrome/Edge/Firefox/Android Chrome: produces audio/webm;codecs=opus
 *   - iOS Safari 14.5+: produces audio/mp4 (note: ElevenLabs Scribe accepts both)
 *   - Requires HTTPS (microphone permission gate)
 */

import { useState, useRef, useCallback, useEffect } from 'react';

const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
];

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const mime of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return ''; // empty string = browser default
}

export function useMediaRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);
  const [durationMs, setDurationMs] = useState(0);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);
  const stopResolveRef = useRef(null);

  const isSupported = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined';

  // Tick duration every 100ms while recording
  useEffect(() => {
    if (!isRecording) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    timerRef.current = setInterval(() => {
      setDurationMs(Date.now() - startedAtRef.current);
    }, 100);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isRecording]);

  const start = useCallback(async () => {
    setError(null);
    setDurationMs(0);
    if (!isSupported) {
      const e = new Error('Microphone recording not supported in this browser');
      setError(e.message);
      throw e;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const mimeType = pickMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const finalType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: finalType });
        chunksRef.current = [];
        // Stop all tracks to release the mic
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
        if (stopResolveRef.current) {
          stopResolveRef.current(blob);
          stopResolveRef.current = null;
        }
      };
      recorder.onerror = (e) => {
        setError(e.error?.message || 'Recorder error');
      };

      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      recorder.start(100); // emit chunks every 100ms
      setIsRecording(true);
    } catch (e) {
      setError(e.message || 'Microphone access denied');
      throw e;
    }
  }, [isSupported]);

  const stop = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }
      stopResolveRef.current = resolve;
      recorder.stop();
      setIsRecording(false);
      recorderRef.current = null;
    });
  }, []);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      stopResolveRef.current = null; // discard the blob
      recorder.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    chunksRef.current = [];
    recorderRef.current = null;
    setIsRecording(false);
    setDurationMs(0);
  }, []);

  return { isRecording, isSupported, start, stop, cancel, durationMs, error };
}
